import { useEffect, useState } from "react";
import get from "lodash/get";

const responseCache = new Map();
const requestCache = new Map();

const getCacheKey = (faqUrl, storyId) => `${faqUrl}::${storyId}`;

export const fetchAiFaqData = async (faqUrl, storyId) => {
  const cacheKey = getCacheKey(faqUrl, storyId);

  if (responseCache.has(cacheKey)) {
    return responseCache.get(cacheKey);
  }

  if (requestCache.has(cacheKey)) {
    return requestCache.get(cacheKey);
  }

  const request = fetch(`${faqUrl}/${encodeURIComponent(storyId)}`)
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`FAQ API failed with status ${response.status}`);
      }

      const data = await response.json();
      const normalizedData = {
        articleId: get(data, ["articleId"], ""),
        headline: get(data, ["headline"], ""),
        slug: get(data, ["slug"], ""),
        heroImageS3Key: get(data, ["hero_image_s3_key"], ""),
        hasFaqs: get(data, ["hasFaqs"], false),
        hasSummary: get(data, ["hasSummary"], false),
        summary: get(data, ["summary"], ""),
        faqs: get(data, ["faqs"], []),
      };

      responseCache.set(cacheKey, normalizedData);
      requestCache.delete(cacheKey);

      return normalizedData;
    })
    .catch((error) => {
      requestCache.delete(cacheKey);
      throw error;
    });

  requestCache.set(cacheKey, request);
  return request;
};

export const useAiFaqData = ({ faqUrl, storyId, enabled }) => {
  const [data, setData] = useState({
    articleId: "",
    headline: "",
    slug: "",
    heroImageS3Key: "",
    hasFaqs: false,
    hasSummary: false,
    summary: "",
    faqs: [],
  });
  const [isLoading, setIsLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled || !faqUrl || !storyId) {
      setData({
        articleId: "",
        headline: "",
        slug: "",
        heroImageS3Key: "",
        hasFaqs: false,
        hasSummary: false,
        summary: "",
        faqs: [],
      });
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    fetchAiFaqData(faqUrl, storyId)
      .then((response) => {
        if (isMounted) {
          setData(response);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        if (isMounted) {
          setData({
            articleId: "",
            headline: "",
            slug: "",
            heroImageS3Key: "",
            hasFaqs: false,
            hasSummary: false,
            summary: "",
            faqs: [],
          });
          setIsLoading(false);
          console.error("Failed to fetch AI FAQ data:", error);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [enabled, faqUrl, storyId]);

  return { ...data, isLoading };
};
